// src/context/NotificationContext.jsx
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notificationService from '../services/notification.service';
import socketService from '../services/socket.service';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);
  const listenersBound = useRef(false);

  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await notificationService.getNotifications(1, 20);
      if (res.success) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (_) {
      // silent — bell just shows last known state
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated) loadNotifications();
    else { setNotifications([]); setUnreadCount(0); }
  }, [isAuthenticated, loadNotifications]);

  // Socket listeners — bind once
  useEffect(() => {
    if (!isAuthenticated) return;
    const socket = socketService.getSocket() || socketService.connect();
    if (!socket || listenersBound.current) return;

    const onNew = (notification) => {
      setNotifications(prev => [notification, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
    };
    const onCount = ({ count }) => setUnreadCount(count);

    socket.on('notification:new', onNew);
    socket.on('notification:count', onCount);
    listenersBound.current = true;

    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:count', onCount);
      listenersBound.current = false;
    };
  }, [isAuthenticated]);

  const markRead = useCallback(async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try { await notificationService.markRead(id); } catch (_) {}
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try { await notificationService.markAllRead(); } catch (_) {}
  }, []);

  const removeNotification = useCallback(async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    try { await notificationService.deleteNotification(id); } catch (_) {}
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, loading,
      loadNotifications, markRead, markAllRead, removeNotification,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export default NotificationContext;